import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface TicketItem {
  quantity: number;
  name: string;
  price: number;
}

interface TicketData {
  companyName: string;
  companyRuc: string;
  companyAddress: string;
  receiptType: 'boleta' | 'factura';
  receiptNumber: string;
  ticketNumber: string;
  date: string;
  time: string;
  items: TicketItem[];
  subtotal: number;
  igv: number;
  igvPercent: number;
  otherCharges: number;
  otherChargesPercent: number;
  valorVenta: number;
  total: number;
  paymentMethod: string;
  amountReceived: number;
  change: number;
  customerName?: string;
  customerDocument?: string;
  documentType?: string;
}

interface TicketReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketData: TicketData | null;
}

// Convertir número a palabras en español
const numberToWords = (num: number): string => {
  const units = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const teens = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const tens = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const hundreds = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  if (num === 0) return 'CERO';
  if (num === 100) return 'CIEN';

  let result = '';
  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);

  if (intPart >= 100) {
    result += hundreds[Math.floor(intPart / 100)] + ' ';
  }

  const remainder = intPart % 100;
  if (remainder >= 10 && remainder < 20) {
    result += teens[remainder - 10];
  } else {
    if (remainder >= 20) {
      result += tens[Math.floor(remainder / 10)];
      if (remainder % 10 !== 0) {
        result += ' Y ' + units[remainder % 10];
      }
    } else if (remainder > 0) {
      result += units[remainder];
    }
  }

  result = result.trim();
  result += ` CON ${decPart.toString().padStart(2, '0')}/100 SOLES`;

  return result;
};

// Generar string QR con formato SUNAT
// Formato: RUC|TIPO_COMPROBANTE|SERIE|NUMERO|IGV|TOTAL|FECHA|TIPO_DOC|NRO_DOC|
const generateSunatQRString = (data: TicketData): string => {
  // Tipo de comprobante: 01 = Factura, 03 = Boleta
  const tipoComprobante = data.receiptType === 'factura' ? '01' : '03';

  // Extraer serie y número del receiptNumber (ej: "B001-00000001" -> serie="B001", numero="00000001")
  const [serie, numero] = data.receiptNumber.split('-');

  // Convertir fecha de DD/MM/YYYY a YYYY-MM-DD
  const dateParts = data.date.split('/');
  const fechaEmision = dateParts.length === 3
    ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
    : data.date;

  // Tipo de documento del cliente: 1 = DNI, 6 = RUC, - = Sin documento
  let tipoDocCliente = '-';
  let nroDocCliente = '-';

  if (data.customerDocument) {
    if (data.customerDocument.length === 11) {
      tipoDocCliente = '6'; // RUC
    } else if (data.customerDocument.length === 8) {
      tipoDocCliente = '1'; // DNI
    }
    nroDocCliente = data.customerDocument;
  }

  // Formato SUNAT: RUC|TIPO|SERIE|NUMERO|IGV|TOTAL|FECHA|TIPO_DOC|NRO_DOC|
  return `${data.companyRuc}|${tipoComprobante}|${serie || 'B001'}|${numero || '00000001'}|${data.igv.toFixed(2)}|${data.total.toFixed(2)}|${fechaEmision}|${tipoDocCliente}|${nroDocCliente}|`;
};

export const TicketReceiptModal: React.FC<TicketReceiptModalProps> = ({
  isOpen,
  onClose,
  ticketData
}) => {
  // Generar el string del QR solo cuando ticketData cambia
  const qrValue = useMemo(() => {
    if (!ticketData) return '';
    return generateSunatQRString(ticketData);
  }, [ticketData]);

  if (!ticketData) return null;

  const handlePrint = () => {
    window.print();
  };

  const receiptTypeLabel = ticketData.receiptType === 'factura'
    ? 'FACTURA DE VENTA ELECTRONICA'
    : 'BOLETA DE VENTA ELECTRONICA';

  const docLabel = ticketData.receiptType === 'factura' ? 'RUC' : 'DNI';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden print:shadow-none print:border-none">
        {/* Botones de acción (no se imprimen) */}
        <div className="flex justify-between items-center p-3 border-b bg-gray-50 print:hidden">
          <span className="font-medium text-sm">Comprobante de Venta</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />
              Imprimir
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Ticket Receipt */}
        <div className="p-4 bg-white font-mono text-xs leading-tight" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
          {/* Header */}
          <div className="text-center mb-3">
            <h1 className="text-xl font-bold tracking-widest mb-1">MACARTUR</h1>
            <p className="text-[10px]">{ticketData.companyName}</p>
            <p className="text-[10px]">RUC: {ticketData.companyRuc}</p>
            <p className="text-[10px]">{ticketData.companyAddress}</p>
          </div>

          {/* Receipt Type */}
          <div className="text-center border-t border-dashed border-gray-400 pt-2 mb-2">
            <p className="font-bold text-[11px]">{receiptTypeLabel}</p>
            <p className="font-bold">{ticketData.receiptNumber}</p>
          </div>

          {/* Ticket Info */}
          <div className="border-t border-dashed border-gray-400 py-2 text-[10px]">
            <p>Ticket: {ticketData.ticketNumber}</p>
            <p>Fecha : {ticketData.date} {ticketData.time}</p>
          </div>

          {/* Items */}
          <div className="border-t border-dashed border-gray-400 py-2">
            {ticketData.items.map((item, index) => (
              <div key={index} className="flex justify-between text-[10px] mb-0.5">
                <span className="flex-1 truncate pr-2">
                  {item.quantity.toString().padStart(2, '0')} {item.name}
                </span>
                <span className="whitespace-nowrap">{item.price.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-dashed border-gray-400 py-2 text-[10px]">
            <div className="flex justify-between">
              <span>Total S/</span>
              <span className="font-bold">{ticketData.total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>IGV {ticketData.igvPercent.toFixed(2)}%</span>
              <span>{ticketData.igv.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>R/Consumo {ticketData.otherChargesPercent.toFixed(2)}%</span>
              <span>{ticketData.otherCharges.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Valor Vta.</span>
              <span>{ticketData.valorVenta.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span>{ticketData.paymentMethod}</span>
              <span>{ticketData.amountReceived.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Vuelto</span>
              <span>{ticketData.change.toFixed(2)}</span>
            </div>
          </div>

          {/* Amount in words */}
          <div className="border-t border-dashed border-gray-400 py-2 text-center text-[10px]">
            <p>{numberToWords(ticketData.total)}</p>
          </div>

          {/* Customer Info */}
          {(ticketData.customerName || ticketData.customerDocument) && (
            <div className="border-t border-dashed border-gray-400 py-2 text-[10px]">
              {ticketData.customerName && (
                <p>CLIENTE: {ticketData.customerName.toUpperCase()}</p>
              )}
              {ticketData.customerDocument && (
                <p>{docLabel}: {ticketData.customerDocument}</p>
              )}
            </div>
          )}

          {/* QR Code SUNAT */}
          <div className="border-t border-dashed border-gray-400 py-3 flex flex-col items-center">
            <QRCodeSVG
              value={qrValue}
              size={96}
              level="M"
              includeMargin={false}
            />
            <p className="text-[7px] text-gray-400 mt-1 max-w-[96px] text-center break-all">
              {qrValue}
            </p>
          </div>

          {/* Footer */}
          <div className="border-t border-dashed border-gray-400 pt-2 text-center text-[9px]">
            <p>Representación impresa de la</p>
            <p className="font-bold">{receiptTypeLabel}</p>
            <p className="mt-1">Consulte su comprobante en:</p>
            <p>www.billy.lilab.pe/macartur</p>
          </div>

          {/* Thank you message */}
          <div className="text-center mt-3 pt-2 border-t border-dashed border-gray-400">
            <p className="text-[10px]">¡Gracias por su preferencia!</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
